import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Timestamp, doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { usePublicQueueData } from '@/hooks/usePublicQueueData';
import { useBarberStatuses } from '@/hooks/useBarberStatuses';
import {
  pendingBreakMinutes,
  isBreakActive,
  isBreakScheduled,
  breakStartLabel,
  breakMinutesForTicket,
} from '@/lib/breaks';
import type { Barber, BarberStatus, BreakReason, QueueTicket, BannerIconName } from '@/types';
import { BREAK_REASON_LABELS } from '@/types';

// ========================================
// Helper Functions
// ========================================

function getInitials(name?: string): string {
  if (!name) return '??';
  return name
    .split(' ')
    .map((w) => w[0])
    .join('.')
    .toUpperCase()
    .slice(0, 4);
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function timestampToDate(ts?: Timestamp): Date | null {
  if (!ts) return null;
  return ts.toDate();
}

function calculateRemainingMinutes(
  serviceStartedAt?: Timestamp,
  serviceDuration?: number
): number | null {
  if (!serviceStartedAt || !serviceDuration) return null;
  const startDate = timestampToDate(serviceStartedAt);
  if (!startDate) return null;

  const elapsed = (Date.now() - startDate.getTime()) / 60000;
  const remaining = serviceDuration - elapsed;
  return Math.max(0, Math.round(remaining));
}

function calculateEstimatedWait(
  ticketIndex: number,
  barberTickets: QueueTicket[],
  status?: BarberStatus | null
): number {
  let totalMinutes = 0;
  for (let i = 0; i < ticketIndex; i++) {
    const t = barberTickets[i];
    if (t.status === 'in_service' || t.status === 'finishing') {
      const remaining = calculateRemainingMinutes(
        t.serviceStartedAt,
        t.serviceDuration
      );
      totalMinutes += remaining ?? 0;
    } else {
      totalMinutes += t.serviceDuration ?? 15;
    }
  }
  // La pausa ocupa un tramo concreto de la jornada: la sufre quien se creó
  // después de marcarla, no todo el que esté esperando (#19, #22). Por fecha,
  // no por minutos estimados (aviso del negocio, 7-09-2026).
  return totalMinutes + breakMinutesForTicket(status, barberTickets[ticketIndex]?.createdAt, totalMinutes);
}

// ========================================
// Banner Icon SVGs
// ========================================

const BANNER_ICONS: Record<BannerIconName, (cls: string) => React.ReactNode> = {
  star: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  ),
  bulb: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
  phone: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  heart: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ),
  scissors: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" />
    </svg>
  ),
  gift: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a4 4 0 00-4-4 4 4 0 004 4zm0 0V6a4 4 0 014-4 4 4 0 01-4 4zm-8 4h16M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
    </svg>
  ),
};

const ICON_COLORS: Record<BannerIconName, string> = {
  star: 'text-accent',
  bulb: 'text-info',
  phone: 'text-success',
  heart: 'text-accent',
  scissors: 'text-accent',
  gift: 'text-warning',
};

// ========================================
// Promotional Carousel
// ========================================

interface CarouselSlide {
  icon?: BannerIconName;
  imageUrl?: string;
  title: string;
  text: string;
}

function PromotionalCarousel({
  t,
  customSlides,
  transitionInterval = 8,
}: {
  t: (key: string) => string;
  customSlides?: CarouselSlide[];
  transitionInterval?: number;
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [fadeClass, setFadeClass] = useState('opacity-100 translate-x-0');

  const tipKeys = [
    'promo_tip_cold_water',
    'promo_tip_growth',
    'promo_tip_regular_cut',
    'promo_tip_beard_care',
  ] as const;

  const [currentTipIndex, setCurrentTipIndex] = useState(0);

  const defaultSlides: CarouselSlide[] = useMemo(
    () => [
      {
        icon: 'star' as BannerIconName,
        title: t('queueDisplay.promo_loyalty_title'),
        text: t('queueDisplay.promo_loyalty_text'),
      },
      {
        icon: 'bulb' as BannerIconName,
        title: t('queueDisplay.promo_tip_title'),
        text: t(`queueDisplay.${tipKeys[currentTipIndex]}`),
      },
      {
        icon: 'phone' as BannerIconName,
        title: t('queueDisplay.promo_app_title'),
        text: t('queueDisplay.promo_app_text'),
      },
      {
        icon: 'heart' as BannerIconName,
        title: t('queueDisplay.promo_social_title'),
        text: t('queueDisplay.promo_social_text'),
      },
    ],
    [t, currentTipIndex]
  );

  const slides = customSlides && customSlides.length > 0 ? customSlides : defaultSlides;
  const totalSlides = slides.length;
  const intervalMs = transitionInterval * 1000;

  useEffect(() => {
    setCurrentSlide(0);
  }, [customSlides]);

  useEffect(() => {
    if (totalSlides <= 1) return;
    const timer = setInterval(() => {
      setFadeClass('opacity-0 translate-x-4');
      setTimeout(() => {
        setCurrentSlide((prev) => {
          const next = (prev + 1) % totalSlides;
          // Rotate tip when using default slides and cycling to tip slide
          if (!customSlides && next === 1) {
            setCurrentTipIndex((ti) => (ti + 1) % tipKeys.length);
          }
          return next;
        });
        setFadeClass('opacity-100 translate-x-0');
      }, 400);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [totalSlides, intervalMs, customSlides]);

  const slide = slides[currentSlide] || slides[0];

  const renderIcon = () => {
    if (slide.imageUrl) {
      return (
        <img
          src={slide.imageUrl}
          alt=""
          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
        />
      );
    }
    if (slide.icon && BANNER_ICONS[slide.icon]) {
      const colorClass = ICON_COLORS[slide.icon] || 'text-accent';
      return (
        <div className="flex-shrink-0">
          {BANNER_ICONS[slide.icon](`w-12 h-12 ${colorClass}`)}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-4">
        <div
          className={`flex items-center gap-4 flex-1 transition-all duration-400 ease-in-out ${fadeClass}`}
        >
          {renderIcon()}
          <div className="min-w-0 truncate">
            <span className="text-accent font-bold text-lg mr-2">
              {slide.title}
            </span>
            <span className="text-ink-subtle text-base">{slide.text}</span>
          </div>
        </div>
        {totalSlides > 1 && (
          <span className="text-ink-muted text-xs flex-shrink-0 tabular-nums">
            {currentSlide + 1}/{totalSlides}
          </span>
        )}
      </div>
      {/* Progress bar */}
      {totalSlides > 1 && (
        <div className="w-full h-0.5 bg-surface-3 rounded-full overflow-hidden">
          <div
            className="h-full bg-accent/60 rounded-full transition-all duration-500"
            style={{ width: `${((currentSlide + 1) / totalSlides) * 100}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ========================================
// Sub-components
// ========================================

const MAX_VISIBLE_WAITING = 8;
const MAX_COLS_PER_ROW = 6;

function BarberLane(props: {
  barber: Barber;
  tickets: QueueTicket[];
  status?: BarberStatus;
  t: (key: string, opts?: Record<string, unknown>) => string;
  _tick: number;
}) {
  const { barber, tickets, status, t } = props;
  // Pausa temporal (comiendo, gestión rápida…): el barbero sigue en pantalla con
  // su cola (issue #19). El distintivo se enciende solo cuando la pausa está
  // ocurriendo; si está comprometida para más tarde se indica la hora, porque
  // decir «comiendo» mientras corta confunde a quien mira desde la sala (#22).
  // Una pausa cuyo instante ya llegó pero que pilla al profesional con alguien
  // en la silla no es «Comiendo»: es «para al terminar este corte». Sin esta
  // distinción el encabezado anunciaba la comida mientras el cuerpo de la misma
  // tarjeta mostraba al cliente siendo atendido (#23).
  const enServicio = tickets.some((t) => t.status === 'in_service' || t.status === 'finishing');
  const onBreak = isBreakActive(status) && !enServicio;
  const breakPending = isBreakScheduled(status) || (isBreakActive(status) && enServicio);
  const breakMinutes = pendingBreakMinutes(status);
  const breakStart = breakStartLabel(status);
  const breakLabel = BREAK_REASON_LABELS[status?.breakReason as BreakReason] || t('queueDisplay.onBreak');
  const currentTicket = tickets.find(
    (t) => t.status === 'in_service' || t.status === 'finishing'
  );
  const waitingTickets = tickets.filter(
    (t) =>
      t.status === 'waiting' ||
      t.status === 'notified' ||
      t.status === 'arrived'
  );
  const overtimePendingTickets = tickets.filter(
    (t) => t.status === 'waiting_overtime_approval'
  );
  const visibleWaiting = waitingTickets.slice(0, MAX_VISIBLE_WAITING);
  const hiddenCount = waitingTickets.length - visibleWaiting.length;

  const remaining = currentTicket
    ? calculateRemainingMinutes(
        currentTicket.serviceStartedAt,
        currentTicket.serviceDuration
      )
    : null;

  // Con una pausa comprometida no se anuncia «sin espera»: quien venga ahora se
  // la come entera. Es el aviso «ven ahora» justo antes de una ausencia (#23).
  const isAvailable = !currentTicket && waitingTickets.length === 0 && !onBreak && !breakPending;

  // Check if first waiting client should get "next up" alert
  // Durante la pausa nadie va a ser llamado hasta que el barbero vuelva: sin "¡Prepárate!".
  const showNextUpAlert =
    remaining !== null && remaining <= 10 && waitingTickets.length > 0 && !onBreak;
  const nextUpTicket = showNextUpAlert ? waitingTickets[0] : null;

  return (
    <div className="bg-surface-3/60 backdrop-blur-sm rounded-xl border border-hairline/50 overflow-hidden flex flex-col border-t-4 border-t-accent shadow-lg shadow-black/20">
      {/* Barber header */}
      <div className="p-4 border-b border-hairline/40 flex items-center gap-3">
        {barber.photoURL ? (
          <img
            src={barber.photoURL}
            alt={barber.displayName}
            className="w-16 h-16 rounded-full object-cover border-2 border-hairline"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-surface-3 flex items-center justify-center text-ink-subtle font-bold text-lg">
            {getInitials(barber.displayName)}
          </div>
        )}
        <div className="min-w-0">
          <h3 className="text-white font-bold text-xl truncate">
            {barber.displayName}
          </h3>
          {barber.specialties?.length > 0 && (
            <p className="text-ink-muted text-xs truncate">
              {barber.specialties.slice(0, 2).join(' · ')}
            </p>
          )}
          {breakPending && (
            <p className="mt-1 text-ink-muted text-sm font-medium" data-testid="tv-break-scheduled">
              {enServicio || !breakStart
                ? t('queueDisplay.pausesAfterCurrent')
                : t('queueDisplay.pausesAt', { time: breakStart })}
            </p>
          )}
          {onBreak && (
            <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-warning/20 border border-warning/40 px-3 py-1 text-warning text-sm font-bold" data-testid="tv-break-badge">
              ☕ {breakLabel} · {breakMinutes > 0 ? t('queueDisplay.backIn', { minutes: breakMinutes }) : t('queueDisplay.backSoon')}
            </p>
          )}
        </div>
      </div>

      {/* Overtime request alert - disappears when barber accepts/rejects */}
      {overtimePendingTickets.map((ticket) => {
        const overtimeMinutes = ticket.overtimeRequest?.overtimeMinutes;
        const workEnd = ticket.overtimeRequest?.barberWorkEnd;
        const serviceEnd = ticket.overtimeRequest?.estimatedServiceEnd;
        return (
          <div
            key={ticket.queueId}
            className="m-3 mb-0 rounded-lg border-2 border-danger/30 from-surface-2 to-surface-2 shadow-lg animate-pulse-glow overflow-hidden"
          >
            <div className="px-3 py-3 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-2xl">🚨</span>
                <span className="text-white font-black text-base sm:text-lg leading-tight tracking-wide">
                  ¡TURNO FUERA DE HORARIO!
                </span>
                <span className="text-2xl">🚨</span>
              </div>
              <p className="text-accent font-semibold text-sm leading-tight">
                {ticket.clientName || 'Cliente'}
                {ticket.serviceName ? ` · ${ticket.serviceName}` : ''}
              </p>
              {(workEnd || overtimeMinutes != null) && (
                <p className="text-danger text-xs mt-1">
                  {workEnd && (
                    <>
                      Fin turno: <strong>{workEnd}</strong>
                    </>
                  )}
                  {workEnd && serviceEnd && ' · '}
                  {serviceEnd && (
                    <>
                      Acaba: <strong>{serviceEnd}</strong>
                    </>
                  )}
                  {overtimeMinutes != null && (
                    <>
                      {' '}(+{overtimeMinutes} min)
                    </>
                  )}
                </p>
              )}
              <p className="text-white text-xs font-bold mt-1.5 uppercase tracking-wider">
                ⚡ Acepta o rechaza desde el móvil
              </p>
            </div>
          </div>
        );
      })}

      {/* Current ticket / status */}
      <div className="p-4 flex-1">
        {isAvailable ? (
          <div className="text-center py-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/40 border border-success/30">
              <span className="w-2.5 h-2.5 rounded-full bg-success animate-pulse" />
              <span className="text-success font-semibold text-sm">
                {t('queueDisplay.available')}
              </span>
            </div>
            <p className="text-ink-muted text-xs mt-3">
              {t('queueDisplay.noWait')}
            </p>
          </div>
        ) : (
          <>
            {/* Pausa temporal: qué pasa y cuánto queda */}
            {onBreak && !currentTicket && (
              <div className="mb-4 rounded-lg border border-warning/40 bg-warning/10 p-3 text-center" data-testid="tv-break-block">
                <p className="text-warning font-bold text-lg">☕ {breakLabel}</p>
                <p className="text-white text-2xl font-black tabular-nums mt-1">
                  {breakMinutes > 0 ? t('queueDisplay.backIn', { minutes: breakMinutes }) : t('queueDisplay.backSoon')}
                </p>
                <p className="text-ink-muted text-xs mt-1">{t('queueDisplay.breakQueueNote')}</p>
              </div>
            )}

            {/* Current service */}
            {currentTicket && (
              <div className="mb-4">
                <div className="text-xs text-ink-muted uppercase tracking-wider mb-2 font-medium">
                  {t('queueDisplay.serving')}
                </div>
                <div className="bg-accent/30 border border-accent/30/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-accent font-bold text-2xl">
                      {currentTicket.ticketNumber}
                    </span>
                    <span className="text-accent text-xl font-bold">
                      {getInitials(currentTicket.clientName)}
                    </span>
                  </div>
                  {currentTicket.serviceName && (
                    <p className="text-ink-subtle text-base truncate">
                      {currentTicket.serviceName}
                    </p>
                  )}
                  {remaining !== null && remaining > 0 && (
                    <p className="text-accent text-xs mt-1">
                      ~{remaining} min {t('queueDisplay.remaining')}
                    </p>
                  )}
                  {currentTicket.status === 'finishing' && (
                    <p className="text-warning text-xs mt-1 font-medium">
                      {t('queueDisplay.finishing')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Next up alert */}
            {nextUpTicket && (
              <div className="mb-4 from-surface-2 to-surface-2 border border-accent/30/50 rounded-lg p-3 animate-pulse-glow">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-accent flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <div className="min-w-0">
                    <span className="text-accent font-bold text-sm">
                      {t('queueDisplay.nextUp')}
                    </span>
                    <span className="text-accent text-sm mx-2">
                      {nextUpTicket.ticketNumber} {getInitials(nextUpTicket.clientName)}
                    </span>
                    <span className="text-accent text-xs font-medium">
                      — {t('queueDisplay.getReady')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Waiting list */}
            <div>
              <div className="text-xs text-ink-muted uppercase tracking-wider mb-2 font-medium">
                {t('queueDisplay.waiting')} ({waitingTickets.length})
              </div>
              {waitingTickets.length === 0 ? (
                <p className="text-ink-muted text-sm">
                  {t('queueDisplay.noOneWaiting')}
                </p>
              ) : (
                <div className="space-y-1.5">
                  {visibleWaiting.map((ticket, i) => {
                    const waitIndex = currentTicket ? i + 1 : i;
                    const allBarberTickets = currentTicket
                      ? [currentTicket, ...waitingTickets]
                      : waitingTickets;
                    const estimatedWait = calculateEstimatedWait(
                      waitIndex,
                      allBarberTickets,
                      status
                    );
                    const isNextUp = i === 0 && showNextUpAlert;

                    return (
                      <div
                        key={ticket.queueId}
                        className={`flex items-center gap-2 text-sm py-1.5 px-2 rounded ${
                          isNextUp
                            ? 'bg-accent/30 border border-accent/30/30'
                            : 'bg-surface-3/50'
                        }`}
                      >
                        <span className="text-ink-muted font-mono w-5 text-right">
                          {i + 1}.
                        </span>
                        <span className="text-ink-subtle font-medium">
                          {ticket.ticketNumber}
                        </span>
                        <span className="text-accent text-base font-bold">
                          {getInitials(ticket.clientName)}
                        </span>
                        {ticket.serviceName && (
                          <span className="text-ink-muted text-xs truncate">
                            {ticket.serviceName}
                          </span>
                        )}
                        <span className="text-ink-muted text-xs ml-auto whitespace-nowrap">
                          ~{estimatedWait} min
                        </span>
                        {isNextUp && (
                          <span className="text-accent text-xs font-medium whitespace-nowrap">
                            {t('queueDisplay.almostYourTurn')}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <p className="text-ink-muted text-xs text-center pt-1">
                      {t('queueDisplay.andMore', { count: hiddenCount })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ========================================
// Main Component
// ========================================

export default function QueueDisplayPage() {
  const { t } = useTranslation();
  const { franchiseId } = useParams<{ franchiseId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const branchParam = searchParams.get('branch');
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(
    branchParam
  );
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Banner config from Firestore
  const [bannerSlides, setBannerSlides] = useState<CarouselSlide[] | undefined>(undefined);
  const [bannerInterval, setBannerInterval] = useState(8);

  const { franchise, branches, barbers, tickets, loading, error, isReconnecting } =
    usePublicQueueData({
      franchiseId: franchiseId || '',
      branchId: selectedBranchId,
    });

  // Auto-select single branch
  useEffect(() => {
    if (branches.length === 1 && !selectedBranchId) {
      setSelectedBranchId(branches[0].branchId);
    }
  }, [branches, selectedBranchId]);

  // Sync branch selection with URL
  useEffect(() => {
    if (selectedBranchId && selectedBranchId !== branchParam) {
      setSearchParams({ branch: selectedBranchId }, { replace: true });
    }
  }, [selectedBranchId, branchParam, setSearchParams]);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Tick every 30s to force countdown recalculation
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  // Banner config (real-time)
  useEffect(() => {
    if (!franchiseId) return;
    const unsub = onSnapshot(
      doc(db, 'banner_configs', franchiseId),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (data.enabled && Array.isArray(data.slides)) {
            const activeSlides = data.slides
              .filter((s: any) => s.isActive)
              .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
              .map((s: any) => ({
                icon: s.icon as BannerIconName | undefined,
                imageUrl: s.imageUrl as string | undefined,
                title: s.title || '',
                text: s.text || '',
              }));
            setBannerSlides(activeSlides.length > 0 ? activeSlides : undefined);
            setBannerInterval(data.transitionInterval || 8);
          } else {
            setBannerSlides(undefined);
            setBannerInterval(8);
          }
        } else {
          setBannerSlides(undefined);
        }
      },
      () => {
        // on error, fall back to defaults
        setBannerSlides(undefined);
      }
    );
    return () => unsub();
  }, [franchiseId]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  }, []);

  // Listen for fullscreen changes (e.g., Escape key)
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Estado en tiempo real de cada barbero (pausas), para la columna (#19)
  const barberStatuses = useBarberStatuses(barbers.map((b) => b.barberId));

  // Group tickets by barber
  const ticketsByBarber = useMemo(() => {
    const map = new Map<string, QueueTicket[]>();
    for (const ticket of tickets) {
      if (!ticket.barberId) continue;
      const list = map.get(ticket.barberId) || [];
      list.push(ticket);
      map.set(ticket.barberId, list);
    }
    return map;
  }, [tickets]);

  // Sort barbers: filter out unavailable, then those with current service first, then by name
  const sortedBarbers = useMemo(() => {
    return [...barbers].filter((b) => b.isAvailable !== false).sort((a, b) => {
      const aTickets = ticketsByBarber.get(a.barberId) || [];
      const bTickets = ticketsByBarber.get(b.barberId) || [];
      const aServing = aTickets.some(
        (t) => t.status === 'in_service' || t.status === 'finishing'
      );
      const bServing = bTickets.some(
        (t) => t.status === 'in_service' || t.status === 'finishing'
      );
      if (aServing && !bServing) return -1;
      if (!aServing && bServing) return 1;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [barbers, ticketsByBarber]);

  const selectedBranch = branches.find(
    (b) => b.branchId === selectedBranchId
  );

  const totalWaiting = tickets.filter(
    (t) =>
      t.status === 'waiting' || t.status === 'notified' || t.status === 'arrived'
  ).length;

  // ========================================
  // Error states
  // ========================================

  if (error === 'franchise_not_found') {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-white text-2xl font-bold mb-2">
            {t('queueDisplay.franchiseNotFound')}
          </h1>
          <p className="text-ink-muted">{t('queueDisplay.checkUrl')}</p>
        </div>
      </div>
    );
  }

  if (error === 'load_error') {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-white text-2xl font-bold mb-2">
            {t('queueDisplay.loadError')}
          </h1>
          <p className="text-ink-muted">{t('queueDisplay.tryAgainLater')}</p>
        </div>
      </div>
    );
  }

  // ========================================
  // Loading state
  // ========================================

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-accent/30 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-ink-subtle">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // ========================================
  // Render
  // ========================================

  return (
    <div className="queue-display-tv h-screen bg-canvas text-white grid grid-rows-[auto_1fr_auto] overflow-hidden relative">
      {/* Animated fire background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Fire glow layers */}
        <div className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 120% 50% at 50% 105%, #ff4500 0%, #ff6a00 15%, #ff8c00 30%, transparent 65%)',
            animation: 'fireGlow1 3s ease-in-out infinite alternate',
          }}
        />
        <div className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 80% 40% at 25% 105%, #ff2200 0%, #ff4500 20%, transparent 55%)',
            animation: 'fireGlow2 4s ease-in-out infinite alternate-reverse',
          }}
        />
        <div className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 80% 40% at 75% 105%, #ff6a00 0%, #ff8c00 20%, transparent 55%)',
            animation: 'fireGlow3 3.5s ease-in-out infinite alternate',
          }}
        />
        {/* Watermark M logo with fire glow */}
        <div className="absolute inset-0 flex items-end justify-center pb-[8%]">
          {/* Flame ring around the M */}
          <div className="relative">
            {/* Outer fire glow */}
            <div className="absolute -inset-16 rounded-full opacity-30"
              style={{
                background: 'radial-gradient(circle, #ff6a00 0%, #ff4500 30%, #ff2200 50%, transparent 70%)',
                animation: 'mFlame1 2.5s ease-in-out infinite alternate',
                filter: 'blur(30px)',
              }}
            />
            {/* Inner fire glow */}
            <div className="absolute -inset-10 rounded-full opacity-25"
              style={{
                background: 'radial-gradient(circle, #ffaa00 0%, #ff6a00 40%, transparent 70%)',
                animation: 'mFlame2 3s ease-in-out infinite alternate-reverse',
                filter: 'blur(20px)',
              }}
            />
            {/* Hot core glow */}
            <div className="absolute -inset-6 rounded-full opacity-20"
              style={{
                background: 'radial-gradient(circle, #ffcc00 0%, #ff8c00 50%, transparent 75%)',
                animation: 'mFlame3 2s ease-in-out infinite alternate',
                filter: 'blur(15px)',
              }}
            />
            {/* The M logo */}
            <img
              src="/icon.jpg"
              alt=""
              className="relative w-[280px] h-[280px] object-cover rounded-full select-none opacity-[0.12]"
              style={{ filter: 'brightness(1.3) saturate(1.2)' }}
              draggable={false}
            />
          </div>
        </div>
      </div>
      {/* Floating ember particles - on top of cards (z-20 > cards z-10) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-20">
        <div className="absolute bottom-0 left-[10%] w-1.5 h-1.5 bg-warning rounded-full"
          style={{ animation: 'ember 4s ease-out infinite', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[25%] w-2 h-2 bg-accent rounded-full"
          style={{ animation: 'ember 5s ease-out infinite 0.8s', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[40%] w-1 h-1 bg-danger rounded-full"
          style={{ animation: 'ember 4.5s ease-out infinite 2s', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[55%] w-1.5 h-1.5 bg-warning rounded-full"
          style={{ animation: 'ember 5.5s ease-out infinite 1.2s', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[70%] w-1 h-1 bg-accent rounded-full"
          style={{ animation: 'ember 3.8s ease-out infinite 3s', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[85%] w-2 h-2 bg-danger rounded-full"
          style={{ animation: 'ember 4.2s ease-out infinite 1.5s', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[5%] w-1 h-1 bg-accent rounded-full"
          style={{ animation: 'ember 6s ease-out infinite 0.3s', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[95%] w-1.5 h-1.5 bg-warning rounded-full"
          style={{ animation: 'ember 4.8s ease-out infinite 2.5s', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[18%] w-1 h-1 bg-danger rounded-full"
          style={{ animation: 'ember 5.2s ease-out infinite 1.8s', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[62%] w-1.5 h-1.5 bg-accent rounded-full"
          style={{ animation: 'ember 4.6s ease-out infinite 0.5s', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[48%] w-2 h-2 bg-warning rounded-full"
          style={{ animation: 'ember 5.8s ease-out infinite 3.5s', filter: 'blur(0.5px)' }}
        />
        <div className="absolute bottom-0 left-[33%] w-1 h-1 bg-danger rounded-full"
          style={{ animation: 'ember 4.3s ease-out infinite 2.2s', filter: 'blur(0.5px)' }}
        />
      </div>
      {/* Fire CSS keyframes */}
      <style>{`
        @keyframes fireGlow1 {
          0% { opacity: 0.12; transform: scaleY(1) scaleX(1); }
          50% { opacity: 0.18; transform: scaleY(1.08) scaleX(1.02); }
          100% { opacity: 0.14; transform: scaleY(0.95) scaleX(0.98); }
        }
        @keyframes fireGlow2 {
          0% { opacity: 0.08; transform: scaleY(1) translateX(0); }
          50% { opacity: 0.14; transform: scaleY(1.1) translateX(2%); }
          100% { opacity: 0.10; transform: scaleY(0.92) translateX(-1%); }
        }
        @keyframes fireGlow3 {
          0% { opacity: 0.08; transform: scaleY(0.95) translateX(0); }
          50% { opacity: 0.13; transform: scaleY(1.12) translateX(-2%); }
          100% { opacity: 0.09; transform: scaleY(1) translateX(1%); }
        }
        @keyframes mFlame1 {
          0% { transform: scale(1) rotate(0deg); opacity: 0.25; }
          33% { transform: scale(1.1) rotate(2deg); opacity: 0.35; }
          66% { transform: scale(0.95) rotate(-1deg); opacity: 0.28; }
          100% { transform: scale(1.05) rotate(1deg); opacity: 0.30; }
        }
        @keyframes mFlame2 {
          0% { transform: scale(1) rotate(0deg); opacity: 0.20; }
          50% { transform: scale(1.15) rotate(-2deg); opacity: 0.30; }
          100% { transform: scale(0.95) rotate(1deg); opacity: 0.22; }
        }
        @keyframes mFlame3 {
          0% { transform: scale(1); opacity: 0.15; }
          50% { transform: scale(1.1); opacity: 0.25; }
          100% { transform: scale(0.9); opacity: 0.18; }
        }
        @keyframes ember {
          0% { transform: translateY(0) translateX(0) scale(1); opacity: 0.7; }
          25% { opacity: 0.9; }
          50% { opacity: 0.5; transform: translateY(-30vh) translateX(15px) scale(0.8); }
          100% { transform: translateY(-70vh) translateX(-10px) scale(0); opacity: 0; }
        }
        html:has(.queue-display-tv), html:has(.queue-display-tv) body {
          overflow: hidden !important;
          scrollbar-width: none !important;
        }
        html:has(.queue-display-tv)::-webkit-scrollbar,
        html:has(.queue-display-tv) body::-webkit-scrollbar,
        .queue-display-tv ::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
        }
        .queue-display-tv * {
          scrollbar-width: none;
        }
      `}</style>
      {/* Header */}
      <header className="bg-surface-3/95 border-b border-hairline px-6 py-3 relative z-10">
        <div className="max-w-[1920px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {franchise?.logo ? (
              <img
                src={franchise.logo}
                alt={franchise.name}
                className="h-12 w-12 rounded object-cover"
              />
            ) : (
              <div className="h-12 w-12 rounded bg-accent flex items-center justify-center text-on-accent font-bold text-lg">
                M
              </div>
            )}
            <div className="min-w-0">
              <h1 className="font-zarathos text-2xl truncate from-surface-2 to-surface-2 bg-clip-text text-transparent">
                {franchise?.name || 'Monkey Barber Club'}
              </h1>
              {selectedBranch && (
                <p className="text-ink-muted text-xs truncate">
                  {selectedBranch.name}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Reconnecting indicator */}
            {isReconnecting && (
              <span className="text-danger text-sm animate-pulse">
                {t('queueDisplay.reconnecting')}
              </span>
            )}
            <div className="text-accent font-mono text-2xl tabular-nums">
              {formatTime(currentTime)}
            </div>
            <button
              onClick={toggleFullscreen}
              className="text-ink-muted hover:text-white transition-colors p-1"
              title={t('queueDisplay.fullscreen')}
            >
              {isFullscreen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Branch tabs (only if multiple branches) */}
      {branches.length > 1 && (
        <div className="bg-surface-3/50 border-b border-hairline px-6 py-2">
          <div className="max-w-[1920px] mx-auto flex gap-2 overflow-x-auto">
            {branches.map((branch) => (
              <button
                key={branch.branchId}
                onClick={() => setSelectedBranchId(branch.branchId)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedBranchId === branch.branchId
                    ? 'bg-accent text-on-accent'
                    : 'bg-surface-3 text-ink-subtle hover:bg-surface-3 hover:text-ink'
                }`}
              >
                {branch.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No branch selected */}
      {!selectedBranchId && branches.length > 1 ? (
        <div className="flex items-center justify-center p-6">
          <div className="text-center">
            <div className="text-5xl mb-4">👆</div>
            <h2 className="text-white text-xl font-bold mb-2">
              {t('queueDisplay.selectBranch')}
            </h2>
            <p className="text-ink-muted">
              {t('queueDisplay.selectBranchDesc')}
            </p>
          </div>
        </div>
      ) : selectedBranchId ? (
        /* Main content - Barber grid */
        <main className="p-6 overflow-hidden relative z-10">
          <div className="max-w-[1920px] mx-auto h-full">
            {sortedBarbers.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-5xl mb-4">💈</div>
                  <h2 className="text-white text-xl font-bold mb-2">
                    {t('queueDisplay.noBarbersActive')}
                  </h2>
                  <p className="text-ink-muted">
                    {t('queueDisplay.noBarbersActiveDesc')}
                  </p>
                </div>
              </div>
            ) : (
              <div
                className="grid gap-6 lg:gap-8 mx-auto w-full h-full auto-rows-fr"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(
                    sortedBarbers.length,
                    MAX_COLS_PER_ROW
                  )}, minmax(0, 1fr))`,
                }}
              >
                {sortedBarbers.map((barber) => (
                  <BarberLane
                    key={barber.barberId}
                    barber={barber}
                    tickets={ticketsByBarber.get(barber.barberId) || []}
                    status={barberStatuses.get(barber.barberId)}
                    t={t}
                    _tick={tick}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      ) : (
        <div />
      )}

      {/* Footer */}
      <footer className="bg-surface-3/95 border-t border-hairline px-6 py-3 relative z-10">
        <div className="max-w-[1920px] mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-shrink-0">
            <span className="text-ink-muted text-sm">
              {t('queueDisplay.totalWaiting')}:{' '}
              <span className="text-accent font-bold text-2xl">{totalWaiting}</span>
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <PromotionalCarousel
              t={t}
              customSlides={bannerSlides}
              transitionInterval={bannerInterval}
            />
          </div>
          <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
            <span className="text-ink-muted text-xs">
              {t('queueDisplay.realTimeUpdates')}
            </span>
            <div className="flex items-center gap-1 opacity-40">
              <span className="text-ink-muted text-[9px]">made with</span>
              <span className="text-danger text-[9px]">&#9829;</span>
              <span className="text-ink-muted text-[9px]">by</span>
              <img src="/logo/c2dev.png" alt="C2 Developers" className="w-3.5 h-3.5 rounded-full" />
              <span className="text-ink-subtle text-[9px] font-medium">C2Developers</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
